import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { Platform } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { fromEvent, interval, Subject, Subscription } from "rxjs";
import { filter, map, take } from "rxjs/operators";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { UserLoginRegistrationResponse } from "src/components/shared-components/enum/enumLoginRegistrationResponse";
import { iCredential } from "src/model/interface/icredential";
import { AccountService } from "src/service/account.service";
import { AlertService } from "src/service/alert.service";
import { AuthenticationService } from "src/service/authentication.service";
import { DpAuthenticationService } from "src/service/dp-authentication.service";
import { EventService } from "src/service/event.service";
import { LoaderService } from "src/service/loader.service";
import { LoggerService } from "src/service/logger.service";
import { SystemRouterService } from "src/service/system-router.service";
import { ThemeService } from "src/service/theme.service";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

@Component({
    selector: "app-form-login",
    templateUrl: "./form-login.component.html",
    styleUrls: ["./form-login.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FormLoginComponent implements OnInit {
  private _logContext: string = "form-login.component";
  private _formSubscriptions: Array<Subscription> = null;
  private _translateSubscription: Subscription = null;
  private _errorUnAuthorizeSubscription: Subscription = null;
  private _keyboardSubscriptions: Subscription = null;
  private _isPerformingLogin: boolean = false;
  private _errorCredentialLoginCount: number = 0;
  private _onPerformUnauthorizeAccess = new Subject<number>();

  //#region Translate variable
  private _lblSettingDownloadFailed: string = "";
  private _lblLoginFailedCredential: string = "";
  private _lblIpWanAccessLock: string = "";
  private _lblLoginMaxTry: string = "";
  private _lblTo: string = "";
  private _lblUserAccessExpiredOn: string = "";
  private _lblUserAccessWillBeActivateOn: string = "";
  private _lblUserNotYetValid: string = "";
  private _lblLoginUnable: string = "";
  private _lblLoginFailed: string = "";
  private _lblLoginFailedLicenceExpired: string = "";
  private _lblPortsErrorInfo: string = "";
  private _lblPortsErrorRequest: string = "";
  private _lblAttention: string = "";
  private _lblPortsError: string = "";
  private _lblTrialExpiring: string = "";
  private _lblTrialExpiringTomorrow: string = "";
  private _lblTrialExpiringToday: string = "";
  private _lblTrialExpired: string = "";
  //#endregion

  public formLogin: UntypedFormGroup = null;
  public forcedActive: boolean = false;
  public cssPlatformClass: string = "";

  @Input() username: string;
  @Input() password: string;
  @Input() rememberMe: boolean;
  @Input() autoLogin: boolean;
  @Input() plantInfoMissiing: boolean;
  @Input() enableButtonLogin: boolean;

  @Output() onRequestToNavigateToPlantSettings: EventEmitter<any> =
    new EventEmitter();
  @Output() onLoginSuccess: EventEmitter<any> = new EventEmitter();

  constructor(
    private _platform: Platform,
    private _fb: UntypedFormBuilder,
    private _alertService: AlertService,
    private _authService: AuthenticationService,
    private _dpAuthService: DpAuthenticationService,
    private _loaderService: LoaderService,
    private _eventService: EventService,
    private _loggerService: LoggerService,
    private _translateService: TranslateService,
    private _routerService: SystemRouterService,
    private _accountService: AccountService,
    private _themeService: ThemeService,
  ) { }

  ngOnInit() {
    this.forcedActive = false;

    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.initializeKeyboardEvent();

    this.initializeLoginForm();
    this.subscribeFormEvent();
    this.initializeErrorUnAuthorizeSubscription();

    this.loadPlatformClass();
  }

  ngOnDestroy() {
    this.unsubscribeFormEvent();

    this._translateSubscription?.unsubscribe();
    this._errorUnAuthorizeSubscription?.unsubscribe();
    this._keyboardSubscriptions?.unsubscribe();
  }

  /**
   * Handle click login button
   *
   * @returns
   */
  public async onClickLogin(): Promise<void> {
    await this.performLoginProcedure();
  }

  /**
   * Navigate to form to create and account
   */
  public onClickCreateAccount() {
    this._routerService.navigateToCreateAccount();
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.settingDownloadFailed",
        "dictionary.loginWrongCredentials",
        "dictionary.ipNotAllowedForConnectionMessage",
        "dictionary.loginExpiredLicence",
        "dictionary.loginFailed",
        "dictionary.loginUnable",
        "dictionary.loginMaxRetry",
        "dictionary.to",
        "dictionary.userAccessExpiredOn",
        "dictionary.userAccessWillBeActivateOn",
        "dictionary.userNotYetValid",
        "dictionary.portsErrorInfo",
        "dictionary.portsErrorActionRequest",
        "dictionary.attention",
        "dictionary.portsErrorMessage",
        "dictionary.trialExpiring",
        "dictionary.trialExpiringTomorrow",
        "dictionary.trialExpiringToday",
        "dictionary.trialExpired",
      ])
      .subscribe((translation) => {
        this._lblSettingDownloadFailed =
          translation["dictionary.settingDownloadFailed"];
        this._lblLoginFailedCredential =
          translation["dictionary.loginWrongCredentials"];
        this._lblIpWanAccessLock =
          translation["dictionary.ipNotAllowedForConnectionMessage"];
        this._lblLoginFailedLicenceExpired =
          translation["dictionary.loginExpiredLicence"];
        this._lblLoginFailed = translation["dictionary.loginFailed"];
        this._lblLoginUnable = translation["dictionary.loginUnable"];
        this._lblLoginMaxTry = translation["dictionary.loginMaxRetry"];
        this._lblTo = translation["dictionary.to"];
        this._lblUserAccessExpiredOn =
          translation["dictionary.userAccessExpiredOn"];
        this._lblUserAccessWillBeActivateOn =
          translation["dictionary.userAccessWillBeActivateOn"];
        this._lblUserNotYetValid = translation["dictionary.userNotYetValid"];
        this._lblPortsErrorInfo = translation["dictionary.portsErrorInfo"];
        this._lblPortsErrorRequest =
          translation["dictionary.portsErrorActionRequest"];
        this._lblAttention = translation["dictionary.attention"];

        this._lblPortsError = translation["dictionary.portsErrorMessage"];
        this._lblTrialExpiring = translation["dictionary.trialExpiring"];
        this._lblTrialExpiringTomorrow =
          translation["dictionary.trialExpiringTomorrow"];
        this._lblTrialExpiringToday =
          translation["dictionary.trialExpiringToday"];
        this._lblTrialExpired = translation["dictionary.trialExpired"];
      });
  }

  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      },
    );
  }
  //#endregion

  /**
   * Recover forgot password, send email
   */
  public async onClickRecoverForgotPwd(): Promise<void> {
    this._routerService.navigateToRequestChangePWD();
  }

  /**
   * Event detection for ENTER keyboard press
   */
  private initializeKeyboardEvent(): void {
    this._keyboardSubscriptions = fromEvent(window, "keyup").subscribe(
      async (evt: KeyboardEvent) => {
        if (evt.key?.toUpperCase() === "ENTER") {
          await this.performLoginProcedure();
        }
      },
    );
  }

  /**
   * Initialize login form plant
   */
  private initializeLoginForm(): void {
    this.formLogin = this._fb.group({
      Username: new UntypedFormControl(this.username, [Validators.required]),
      Password: new UntypedFormControl(this.password, [Validators.required]),
      AutoLogin: new UntypedFormControl(this.autoLogin),
      RememberMe: new UntypedFormControl(this.rememberMe),
    });
    if (this.autoLogin === undefined) {
      this.autoLogin = false;
    }
    this.isRememberMeDependent(StaticClass.booleanize(this.autoLogin));
  }

  /**
   * Subscribe value change event from each form field.
   */
  private subscribeFormEvent(): void {
    this._formSubscriptions = new Array<Subscription>();

    this._formSubscriptions.push(
      this.formLogin
        .get("AutoLogin")
        .valueChanges.subscribe((formUpdatedField: boolean) => {
          this.isRememberMeDependent(formUpdatedField);
        }),
    );
  }

  /**
   * This method implements dependency relation  "autoLoginValue |= RememberMe".
   * If the first checkbox is set to TRUE, then the second one is also set to TRUE and it is disabled.
   *
   * If the first checkbox is set to FALSE, then the second one REMAINS TRUE (as it was), while it is re-enabled.
   *
   * @param autoLoginValue Current value of "AutoLogin" checkbox
   */
  private isRememberMeDependent(autoLoginValue: boolean): void {
    if (autoLoginValue) {
      this.formLogin.patchValue(
        {
          RememberMe: autoLoginValue,
        },
        { emitEvent: false },
      );
      this.forcedActive = true;
    } else {
      this.forcedActive = false;
    }
  }

  /**
   * Perform login proedure. It componse authentication object, then dispatch "onMakeLogin" event
   *
   * @returns
   */
  private async performLoginProcedure(): Promise<void> {
    if (!this.formLogin.valid) {
      return;
    }

    const userCredential: iCredential = {
      Username: this.formLogin.value.Username,
      Password: this.formLogin.value.Password,
      RememberMe: this.formLogin.value.RememberMe,
      isAutoLoginOn: this.formLogin.value.AutoLogin,
      IsSiboDevice: false,
    };

    await this.performDigitalPlaceLogin(userCredential);
  }

  /**
   * Perform digital place login operation
   *
   * @param userCredentials User credentials
   */
  private async performDigitalPlaceLogin(
    userCredentials: iCredential,
  ): Promise<void> {
    try {
      let email = "";
      await this._loaderService.presentLoaderWaiting();

      const loginDataResult = await this._dpAuthService.login(userCredentials);
      await this._loaderService.dismissLoaderWaiting();

      if (loginDataResult) {
        this._errorCredentialLoginCount = 0;

        await this.presentTrialExpirationWarning();

        this.onLoginSuccess.emit(userCredentials);
      } else {
        throw loginDataResult;
      }
    } catch (exception) {
      await this._loaderService.dismissLoaderWaiting();

      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "performDigitalPlaceLogin()",
        "Error in login: " + (exception?.message ?? JSON.stringify(exception)),
      );
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "performDigitalPlaceLogin()",
        "errorCode: " +
        exception.error.errorCode +
        " | extraInfo: " +
        JSON.stringify(exception.error.extraInfo),
      );

      /* The administrator has not been approved and its free trial period is over:
         a dedicated popup is presented and the unauthorized counter is not
         incremented, since the credentials are correct */
      if (
        exception?.error?.errorCode ===
        UserLoginRegistrationResponse.USER_ADMINISTRATOR_EXPIRED
      ) {
        await this._alertService.presentAlertOk(
          this._lblAttention,
          "",
          this._lblTrialExpired,
        );

        return;
      }

      let registrationResponse = exception.error.errorCode;
      if (exception?.status === 401) {
        registrationResponse = UserLoginRegistrationResponse.USER_NOT_FOUND;
      }
      /*if (
        registrationResponse ===
        UserLoginRegistrationResponse.USER_MUST_RESET_PASSWORD
      ) {
        if (!exception.error) {
          exception.error = {};
        }

        if (!exception.error.extraInfo) {
          exception.error.extraInfo = {};
        }
        exception.error.extraInfo["email"] = userCredentials.Username;
      }*/

      await this._alertService.presentAlertDigitalAccountManage(
        registrationResponse,
        exception.error.extraInfo,
      );

      if (exception?.status === 401) {
        this._errorCredentialLoginCount++;
        this._onPerformUnauthorizeAccess.next(this._errorCredentialLoginCount);
      }
    } finally {
      this._isPerformingLogin = false;
    }
  }

  /**
   * Present the warning popup when the logged administrator is still on its trial
   * period and the period is going to expire.
   *
   * Nothing is shown when the user is not an administrator, when the account is not
   * a trial one or when the trial period is already expired.
   */
  private async presentTrialExpirationWarning(): Promise<void> {
    const trialDaysLeft: number | null =
      this._dpAuthService.getTrialDaysLeft();

    if (trialDaysLeft === null) {
      return;
    }

    let trialMessage: string = this._lblTrialExpiringToday;

    if (trialDaysLeft === 1) {
      trialMessage = this._lblTrialExpiringTomorrow;
    } else if (trialDaysLeft > 1) {
      trialMessage = this._lblTrialExpiring.replace(
        "XX",
        trialDaysLeft.toString(),
      );
    }

    await this._alertService.presentAlertOk(this._lblAttention, "", trialMessage);
  }

  /**
   * Perform BIGStudio standard login procedure
   *
   * @param userCredentials
   */
  private async performBIGStudioLogin(
    userCredentials: iCredential,
  ): Promise<void> {
    if (!this.plantInfoMissiing) {
      this._isPerformingLogin = true;

      await this._loaderService.presentLoaderWaiting();
      await this.executeBIGStudioLogin(userCredentials);
      /* Open alert if  Api-port and mqtt-port are assigned unmmissible values*/
    } else {
      await this.presentPopupInvalidPort();
    }
  }

  /**
   * Perform BIGStudio login operation with specific credentials
   *
   * @param loginCredential Login credentials object
   */
  private async executeBIGStudioLogin(
    loginCredential: iCredential,
  ): Promise<void> {
    try {
      const loginDataResult = await this._authService.login(loginCredential);

      if (loginDataResult) {
        this._isPerformingLogin = false;
        this._errorCredentialLoginCount = 0;

        this.onLoginSuccess.emit(loginCredential);
      } else {
        throw new Error();
      }
    } catch (exception) {
      await this._authService.handleUnthorizedLogin(exception);
      if (exception?.status === 401) {
        this._errorCredentialLoginCount++;
        this._onPerformUnauthorizeAccess.next(this._errorCredentialLoginCount);

        this._isPerformingLogin = false;
      }
    }
  }

  /**
   * Handle un authorized errorr response
   *
   * @param error
   */
  /*
  private async handleUnthorizedLogin(error: HttpErrorResponse): Promise<void> {
    this._loggerService.log(
      LogLevel.LOG_ERROR,
      this._logContext,
      this._logContext,
      "handleUnthorizedLogin()",
      "ERROR STATUS: " + error.status + " | msg: " + error.message
    );

    let stringTextAlert = this._lblSettingDownloadFailed;
    //close loader popup

    await this._loaderService.dismissLoaderWaiting();

    if (error?.status === 401) {
      stringTextAlert = this.get401ErrorResponseText(error?.error);

      this._errorCredentialLoginCount++;
    } else if (error?.status === 503) {
      stringTextAlert = this._lblLoginFailedLicenceExpired;
    } else {
      stringTextAlert = this._lblLoginFailed;
    }

    //present error popup
    await this._alertService.clearAlertOk();

    this._alertService
      .presentAlertOk(this._lblLoginUnable, "", stringTextAlert)
      .then(() => {
        //propagate current error count
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "alertService.presentAlertOk",
          "handleUnthorizedLogin",
          "Unsuccesfull login counter: " + this._errorCredentialLoginCount
        );

        this._onPerformUnauthorizeAccess.next(this._errorCredentialLoginCount);

        this._isPerformingLogin = false;
      });
  }
  */

  /**
   * Return text for 401 error response
   *
   * @param authErrorObject Authentication error object
   * @returns
   */
  /*
  private get401ErrorResponseText(authErrorObject: AuthError): string {
    let stringTextAlert = this._lblLoginFailedCredential;

    if (authErrorObject) {
      switch (authErrorObject.code) {
        case UnthorizedAccessType.WAN_BLOCK:
          stringTextAlert = this._lblIpWanAccessLock;
          break;
        case UnthorizedAccessType.USER_EXPIRED:
          stringTextAlert = this.getStringTextFromUserExpiredError(
            authErrorObject.extraInfo["EndValidity"]
          );
          break;
        case UnthorizedAccessType.USER_NOT_YET_VALID:
          stringTextAlert = this.getStringTextFromUserNotYetValidError(
            authErrorObject.extraInfo["StartValidity"]
          );

          break;
      }
    }

    return stringTextAlert;
  }
  */

  /**
   * Return formatted string text where user is not yet valid
   *
   * @param userStartValidity User start validity
   * @returns Formatted user not yet valid text
   */
  /*
  private getStringTextFromUserNotYetValidError(
    userStartValidity: string
  ): string {
    const date: string = moment(
      userStartValidity,
      MomentFormats.MOMENT_DATETIME_API_IT_CULTURE_INFO
    ).format(MomentFormats.MOMENT_DATETIME_DATE_EXTENDED);
    const time: string = moment(
      userStartValidity,
      MomentFormats.MOMENT_DATETIME_API_IT_CULTURE_INFO
    ).format(MomentFormats.MOMENT_DATETIME_FORMAT_LONG_HOUR_MINUTES);
    const year: number = moment(
      userStartValidity,
      MomentFormats.MOMENT_DATETIME_API_IT_CULTURE_INFO
    ).year();

    // Workaround to present generic message when user is not still
    // active and start validity year is before 2020
    if (year <= 2020) {
      return this._lblUserNotYetValid;
    }

    return (
      this._lblUserAccessWillBeActivateOn +
      " " +
      date +
      " " +
      this._lblTo +
      " " +
      time
    );
  }
*/

  /**
   * Return formatted string text where user is expired
   *
   * @param userEndValidity User expired date
   * @returns Formatted  user expired text
   */
  /*
  private getStringTextFromUserExpiredError(userEndValidity: string): string {
    const date: string = moment(
      userEndValidity,
      MomentFormats.MOMENT_DATETIME_API_IT_CULTURE_INFO
    ).format(MomentFormats.MOMENT_DATETIME_DATE_EXTENDED);
    const time: string = moment(
      userEndValidity,
      MomentFormats.MOMENT_DATETIME_API_IT_CULTURE_INFO
    ).format(MomentFormats.MOMENT_DATETIME_FORMAT_LONG_HOUR_MINUTES);
    const year: number = moment(
      userEndValidity,
      MomentFormats.MOMENT_DATETIME_API_IT_CULTURE_INFO
    ).year();

    // Workaround to present generic message when user is not still
    // active and end validity year is before 2020
    if (year <= 2020) {
      return this._lblUserNotYetValid;
    }

    return (
      this._lblUserAccessExpiredOn + " " + date + " " + this._lblTo + " " + time
    );
  }
  */

  /**
   *
   * Handle Unauthorized and event counter and show in certain case a countdown popup for blocking temporary new access.
   * It's a copy of Ios function for blocking Unathorized access
   *
   */
  private initializeErrorUnAuthorizeSubscription() {
    this._errorUnAuthorizeSubscription = this._onPerformUnauthorizeAccess
      .pipe(
        // filter errorCount value when it's 0 and it isn't multiple of NUMBER_ALLOW_UNAUTHORIZE_LOGIN_BEFORE_BLOCK params
        filter(
          (errorCount) =>
            errorCount %
            AppSettings.NUMBER_ALLOW_UNAUTHORIZE_LOGIN_BEFORE_BLOCK ===
            0 && errorCount !== 0,
        ),
        /**
         * Trasform errorCount to factor passed subsequently to custom countdown costruct
         * Es.
         * errorCount: 5 -> return 1
         * errorCount: 10 -> return 2
         * errorCount: 15 -> return 3
         * errorCount: 20 -> return 4
         */
        map((errorCount) => {
          return (
            errorCount / AppSettings.NUMBER_ALLOW_UNAUTHORIZE_LOGIN_BEFORE_BLOCK
          );
        }),
      )
      .subscribe(async (moltiplicator: number) => {
        //on subscribtion it showing popuo loader with text. I set also duration for enabling autoclosing loader function by specific time
        const timerDuration = 5 * moltiplicator;

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "_onPerformUnauthorizeAccess",
          "TimerDuration: " + timerDuration,
        );

        let loaderBlockText: string = this._lblLoginMaxTry.replace(
          "XX",
          timerDuration.toString(),
        );

        await this._loaderService.presentLoaderBlockAuthentication(
          loaderBlockText,
          timerDuration * 1000,
        );

        /**
         * Custom countdown costruct:
         * interval: emit a value each x ms (1 sec in this case). Value emit 0,1,2,3
         * take: take x value of emission: in this case the entire duration duration of countdown: if countdown last 30 seconds, take function parameter will be 30
         * map: i invert value from interval function form making countd (ex 0->30, 1->29, 2->28...)
         * filter: filter until value is grater then 0
         *
         * On subscribe function, i update loader popup text
         *
         */
        interval(1000)
          .pipe(
            take(timerDuration),
            map((actualValueTimer: number) => {
              return timerDuration - (actualValueTimer + 1);
            }),
            filter((actualValueTimer) => actualValueTimer > 0),
          )
          .subscribe((actualValueTimer: number) => {
            loaderBlockText = this._lblLoginMaxTry.replace(
              "XX",
              actualValueTimer.toString(),
            );

            this._loaderService.messageLoaderBlockAutentication =
              loaderBlockText;
          });
      });
  }

  /**
   * Load platform class
   */
  private loadPlatformClass(): void {
    this.cssPlatformClass = this._platform.is("ios") ? "ios" : "";
  }

  /**
   * Present popup invalid port
   */
  private async presentPopupInvalidPort(): Promise<void> {
    try {
      const goToPlantSetting: boolean =
        await this._alertService.presentAlertConfirmOrDismiss(
          this._lblAttention,
          this._lblPortsError,
          "",
        );

      /* If "Confirm" is selected, we rare re-routed to "plant-settings" page */
      if (goToPlantSetting) {
        this.onRequestToNavigateToPlantSettings.emit();
      }
    } catch (exception) {
      await this._loaderService.dismissLoaderWaiting();

      await this._alertService.presentAlertSomethingWrong();
    } finally {
      this._isPerformingLogin = false;
    }
  }

  /**
   * Unsubscribe to form event events
   */
  private unsubscribeFormEvent(): void {
    for (const sub of this._formSubscriptions) {
      sub.unsubscribe();
    }
  }
}
